/**
 * components/TxnCostCells.js
 * Three shared table cells for Penjualan's/Pembelian's dashboards — `SubtotalBarangCell`,
 * `RincianTambahanCell`, `TotalSetelahBiayaCell` — added by the "Nilai column split" (Brief 3
 * of the sidebar/dashboard redesign set) alongside the trimmed `TxnNilaiCell` (Total Nilai) to
 * replace the old single "Nilai" column with four: Subtotal Barang → Rincian Tambahan → Total
 * Nilai → Total Setelah Biaya — the same story `TransactionDetailModal`'s own money cards
 * already tell top-to-bottom, told left-to-right on the dashboard instead. Grouped in one file
 * (mirroring `TxnBadges.js`'s own `StatusPill`+`DueBadge` precedent) since all three are small,
 * tightly related (all read `txn.lines`/`txn.costItems`), and always used together at the same
 * call sites. Each owns its whole `<td>`, same "one component, one `<td>`, invoked inline in
 * the row" convention `TxnBarangCell`/`TxnNilaiCell` already establish — never a single
 * fragment-returning multi-`<td>` component. All three take `{ txn }` only — every underlying
 * helper (`goodsTotal`/`totalDikurangiBiaya`/`totalDitambahBiaya`/the cost-item sum helpers)
 * takes a bare `txn`/`txn.costItems`, and per-line/per-cost-item descriptions live directly on
 * those objects, so no `byCode` or other lookup prop is needed.
 *
 * **Color convention across the three**: Subtotal Barang and Total Setelah Biaya are both
 * neutral black/bold (`var(--text)`) — deliberately, confirmed with Chalton: color in this app
 * means STATUS (Lunas/overdue/raises-debt/lowers-debt), and these two are supporting
 * pre-/post-adjustment figures, not a status. Coloring either the same as Total Nilai's own
 * type-based green/red/blue-deep would make it read as a second "real total," undermining the
 * whole point of this split (making it unambiguous which number is the actual bottom line) —
 * `TxnNilaiCell`'s Total Nilai is the ONE column that carries status color. Rincian Tambahan's
 * own net number and per-item lines DO carry color, but by SIGN/TYPE, not by transaction type —
 * see `RincianTambahanCell`'s own comment below.
 *
 * **No per-cost-item-type color convention existed anywhere in this codebase before this
 * change** (checked: `TransactionDetailModal.js`'s own "Rincian Tambahan" card renders every
 * cost item type identically, a neutral chip + plain black amount; `CostItemsEditor.js` has no
 * type-color mapping either) — the red/green-by-type convention `RincianTambahanCell` introduces
 * below is NEW, reusing the same `var(--red)`/`var(--green)` tokens the net number itself uses,
 * not a rediscovery of an existing rule.
 *
 * **Dashboard alignment fix**: every per-item breakdown line (`{name}: Rp {amount}`) used to be
 * `white-space: nowrap` (`styles.css`) — on real data this let one long item name force its
 * column to whatever width avoided wrapping that single line, at the expense of Barang/Klien
 * (see `styles.css`'s own header comment on `.subtotal-cell__items`/etc. for the full
 * investigation). These lines now wrap normally; the space between `"Rp"` and the amount is a
 * genuine non-breaking space (`NBSP`, reused from `utils/pagination.js` — the existing canonical
 * source for this exact "never split this specific two-token pair across a wrap" problem, not a
 * second hand-typed constant) so a long item name can wrap freely while the price itself never
 * splits mid-number.
 *
 * **Vertical alignment (dashboard alignment fix, round 3)**: `RincianTambahanCell`/
 * `TotalSetelahBiayaCell`'s own `<td>`s (`.rincian-cell`/`.biaya-cell`) are now UNCONDITIONALLY
 * vertically centered — real content or the empty "—" dash alike — via `styles.css`'s shared
 * center-line group, replacing a round-2-era conditional rule (top-aligned by default, only
 * the empty case centered). `SubtotalBarangCell`'s own `<td>` (`.subtotal-cell`) deliberately
 * stays OUT of that group, unchanged — it always has real content and needs a consistent top
 * edge for its own item breakdown. Purely a CSS change; none of these three components render
 * anything differently as a result.
 *
 * **Tagihan & Potongan rename task**: `RincianTambahanCell`'s own net-sign/per-item color
 * ternaries were extracted to `utils/costItems.js`'s `costItemColor`/`costItemNetColor` (a pure
 * refactor, byte-identical output) so `TransactionDetailModal.js`'s own new "Tagihan & Potongan"
 * box can reuse the exact same rule instead of a second, independently-drifting copy.
 *
 * **Pembelian batch 2 (transfer support) — `TotalSetelahBiayaCell` gains an optional
 * `perspective` prop.** `txn.type` alone stopped being enough to pick "Total Dikurangi Biaya"
 * vs. "Total Ditambah Biaya" the instant a TRANSFER could be shown on EITHER Penjualan or
 * Pembelian — the wording must follow which page is hosting the row (a sale-shaped accrual on
 * Penjualan, a purchase-shaped one on Pembelian), not the transaction's own fixed type.
 * `perspective: "PENJUALAN"|"PEMBELIAN"|undefined` — Penjualan.js/Pembelian.js now both pass it
 * explicitly; PiutangUtang.js's own call site is untouched and keeps omitting it, which falls
 * back to the exact old `txn.type === "PEMBELIAN"` check, byte-for-byte (that page mixes all
 * three types with no single fixed per-page perspective to assign a TRANSFER row to — fixing
 * its own whole-business PIUTANG/UTANG convention for TRANSFER is separately deferred, see
 * root CLAUDE.md). `SubtotalBarangCell`/`RincianTambahanCell` never branched on type at all, so
 * neither needs this prop.
 */
import React from "react";
import { fmtQty, fmtDelta } from "../utils/idGenerators";
import { goodsTotal, totalDikurangiBiaya, totalDitambahBiaya } from "../utils/txnStatus";
import { sumCostItemsRaisingTotal, sumPotonganCostItems, costItemColor, costItemNetColor } from "../utils/costItems";
import { shouldShowBucketBars, barangSisa, barangPctPaid, tagihanPotonganSisa, tagihanPotonganPctPaid } from "../utils/paymentSplit";
import { NBSP } from "../utils/pagination";
import { PaymentProgress } from "./TxnNilaiCell";
import { useExpenses } from "./ExpensesContext";
import { expensesForTxn, sumExpenses, expenseLabel } from "../utils/expenses";

/**
 * Subtotal Barang — the goods-only total (`goodsTotal`, never re-derived here) plus the
 * per-item breakdown moved out of `TxnNilaiCell`'s own old "Nilai" column. Reuses the EXACT
 * original breakdown fallback chain (`l.name || l.internalCode` — no `byCode` lookup, unlike
 * `TxnBarangCell`'s own richer fallback) so relocating it doesn't silently change behavior. A
 * persisted transaction always has ≥1 line (`TransactionFormModal`'s own `canSubmit` requires
 * it), so there's no reachable empty case here — no "—" branch, matching `TxnNilaiCell`'s own
 * prior lack of one.
 */
export function SubtotalBarangCell({ txn }) {
  const lines = txn.lines || [];
  return (
    <td className="subtotal-cell">
      <div className="subtotal-cell__stack">
        <div className="subtotal-cell__total">{fmtQty(goodsTotal(txn))}</div>
        <div className="subtotal-cell__items">
          {lines.map((l, i) => (
            <div key={i}>{l.name || l.internalCode}: Rp{NBSP}{fmtQty(l.qty * l.unitPrice)}</div>
          ))}
        </div>
        {shouldShowBucketBars(txn) && (
          <>
            <div className="subtotal-cell__sisa">Sisa: Rp{NBSP}{fmtQty(barangSisa(txn))}</div>
            <PaymentProgress pct={barangPctPaid(txn)} />
          </>
        )}
      </div>
    </td>
  );
}

/**
 * Rincian Tambahan — Tagihan/Tagihan Supplier/Potongan items ONLY (Biaya deliberately
 * excluded — it lives in `TotalSetelahBiayaCell` instead), plus an aggregate SIGNED net number
 * (`Σtagihan − Σpotongan` — the exact expression already inline inside `utils/txnStatus.js`'s
 * own `txnTotal`, composed here rather than exported as a new helper since the brief only asks
 * to reuse the existing named sums, not add new ones). Net colored by sign: positive (raises
 * what's owed) = red, negative (lowers it) = green, exactly zero = neutral black/bold (a
 * net-zero result means Tagihan and Potongan cancelled out — "nothing happened," not a genuine
 * reduction, so it doesn't earn green). Individual line items are compact, ONE line each, no
 * type-label text at all (color alone conveys Tagihan-vs-Potongan unambiguously — confirmed
 * with Chalton, deliberately more compact than `TransactionDetailModal`'s own chip-badge
 * convention, since this whole brief exists to reclaim dashboard space).
 */
export function RincianTambahanCell({ txn }) {
  const costItems = txn.costItems || [];
  const relevant = costItems.filter(
    (c) => c.type === "tagihan" || c.type === "tagihanSupplier" || c.type === "potongan"
  );
  if (relevant.length === 0) {
    return <td className="rincian-cell rincian-cell--empty">—</td>;
  }

  const net = sumCostItemsRaisingTotal(costItems) - sumPotonganCostItems(costItems);
  const netColor = costItemNetColor(net);

  return (
    <td className="rincian-cell">
      <div className="rincian-cell__stack">
        <div className="rincian-cell__net" style={{ color: netColor }}>{fmtDelta(net)}</div>
        {shouldShowBucketBars(txn) && (
          <>
            <div className="rincian-cell__sisa">Sisa: Rp{NBSP}{fmtQty(tagihanPotonganSisa(txn))}</div>
            <PaymentProgress pct={tagihanPotonganPctPaid(txn)} />
          </>
        )}
        <div className="rincian-cell__items">
          {relevant.map((c) => (
            <div key={c.id} style={{ color: costItemColor(c.type) }}>
              {c.description}: Rp{NBSP}{fmtQty(c.amount)}
            </div>
          ))}
        </div>
      </div>
    </td>
  );
}

/**
 * Total Setelah Biaya — Biaya-only items, plus the derived final number: `totalDitambahBiaya`
 * for PEMBELIAN (money only ever flows out, so Biaya there is a second outflow to ADD),
 * `totalDikurangiBiaya` for PENJUALAN/TRANSFER (reused verbatim, never re-derived here). The
 * column header stays neutral ("Total Setelah Biaya") regardless of direction, and the cell
 * shows a bare bold number like every OTHER money column's headline (`.subtotal-cell__total`,
 * `.rincian-cell__net`, `.nilai-cell__total`). The small "Total Dikurangi Biaya"/"Total
 * Ditambah Biaya" caption that used to sit above it was removed on Chalton's request
 * (2026-10-11) — the column header already names the figure; the Detail window keeps the
 * wording on its own final line, where the label is what explains the number. The direction
 * itself (`perspective`) still decides which total is computed. The
 * final line and the item lines render with NO color style at all (plain/dark, inherits
 * `var(--text)` from CSS) — explicitly not green/red/orange, since Biaya is informational here,
 * not a status (see this file's own header comment).
 */
export function TotalSetelahBiayaCell({ txn, perspective }) {
  // Biaya Lain-Lain (2026-10-11): this transaction's own costs live in `data.expenses` (read via
  // ExpensesContext; empty outside App's provider). Any leftover in-transaction "biaya" row
  // (none after the v26 migration) is still shown, so nothing is ever silently hidden.
  const { expenses } = useExpenses();
  const linked = expensesForTxn(expenses, txn.id);
  const biayaItems = [
    ...(txn.costItems || []).filter((c) => c.type === "biaya"),
    ...linked.map((e) => ({ id: e.id, description: expenseLabel(e), amount: e.amount })),
  ];
  if (biayaItems.length === 0) {
    return <td className="biaya-cell biaya-cell--empty">—</td>;
  }

  const isPembelian = perspective ? perspective === "PEMBELIAN" : txn.type === "PEMBELIAN";
  const expenseTotal = sumExpenses(linked);
  const final = isPembelian ? totalDitambahBiaya(txn, expenseTotal) : totalDikurangiBiaya(txn, expenseTotal);

  return (
    <td className="biaya-cell">
      <div className="biaya-cell__stack">
        <div className="biaya-cell__final">{fmtQty(final)}</div>
        <div className="biaya-cell__items">
          {biayaItems.map((c) => (
            <div key={c.id}>{c.description}: Rp{NBSP}{fmtQty(c.amount)}</div>
          ))}
        </div>
      </div>
    </td>
  );
}
